import { actingAs } from '../api/act-as'
import type { ApiFailure } from '../api/types'
import { Button, CorrelationId, relativeTime } from './ui'

/**
 * Renders each API failure kind as the screen it actually deserves.
 *
 * This component is where the backend's central design decision becomes visible
 * to a person. The API refuses to answer "you have no permissions" with an empty
 * 200, precisely so this screen can exist: a user who is misconfigured is told
 * they are misconfigured, and is given the reference number that lets someone
 * fix it. Collapsing these into one "something went wrong" would throw away the
 * distinction the whole authorization model was built to preserve.
 */
export function FailureState({ failure, onRetry }: { failure: ApiFailure; onRetry?: () => void }) {
  switch (failure.kind) {
    case 'no-permissions': {
      // Under Login As the reader is the ADMINISTRATOR, and the account with no
      // rows is the person they are acting as. The API's NoPermissions reply
      // names nobody, so "your account" would point the admin at themselves and
      // tell them to ask themselves for access — the misreading RefusalFor
      // (ReportGuards.cs) words around for every other refusal. actingAs() is
      // this tab's module state, not a hook.
      const acting = actingAs() !== null
      return (
        <Panel tone="warn" title={acting ? 'This person has no data permissions' : 'You have no data permissions'}>
          {acting ? (
            <>
              <p>
                The person you are signed in as is recognised, but has no rows in the permission table —
                so there is no company, business unit or team they may be shown.
              </p>
              <p className="mt-2">
                This is a configuration gap for them, not an empty report. Their access is granted in
                D365 (D365_UserPermDetails); the reference below identifies this request.
              </p>
            </>
          ) : (
            <>
              <p>
                Your account is signed in and recognised, but it has no rows in the permission table — so
                there is no company, business unit or team it is allowed to show you.
              </p>
              <p className="mt-2">
                This is a configuration gap, not an empty report. Send the reference below to whoever
                administers MBI permissions and they can grant your access.
              </p>
            </>
          )}
          <CorrelationId value={failure.correlationId} />
        </Panel>
      )
    }

    case 'forbidden':
      /* THE NEW MODEL'S ONE SENTENCE (03 rule 4; 05 6.4). The title is the whole of
         it for a reader; an administrator signed in as the person also gets what
         stopped them, and the code. No D365 table is named: it decides nothing then. */
      if (failure.reason === 'DENIED_REPORT') {
        return (
          <Panel tone="warn" title="Access denied">
            <p>Contact your MBI administrator.</p>
            {failure.adminReason ? (
              <p className="mt-2 text-(length:--text-small) text-(--color-ink-3)">
                <span className="font-semibold text-(--color-ink-2)">Why (only you can see this):</span>{' '}
                {failure.explanation ? `${failure.explanation} ` : ''}({failure.adminReason})
              </p>
            ) : null}
            <CorrelationId value={failure.correlationId} />
          </Panel>
        )
      }
      return (
        <Panel tone="warn" title={failure.title}>
          {/* The API's sentence for THIS refusal. It used to be one hardcoded
              line about platform roles, printed under every 403 title — so
              "Not on the access list for this report" was explained by a
              sentence about roles, naming a fix that would not have helped.
              The fallback stays generic on purpose: inventing a specific reason
              we were not told is how the first version went wrong. */}
          <p>{failure.explanation ?? 'This account is not permitted to open this page.'}</p>
          <CorrelationId value={failure.correlationId} />
        </Panel>
      )

    // THE RESIDUAL PATH, since 17 Sep 2026.
    //
    // A maintenance window now replaces the screen before a page ever runs:
    // App renders PlatformClosed or ReportClosed from the state /api/v1/me
    // carries, and the api client announces a late window so an interrupted
    // session is replaced too. What still reaches here is the narrow case of a
    // 503 on a surface that is not a report — and it is kept, because a refusal
    // that renders as "something went wrong" sends the reader to the wrong
    // person. Do not delete it as a duplicate of MaintenanceScreen; it is the
    // floor under it.
    case 'maintenance':
      return (
        <Panel tone="warn" title="Closed for maintenance">
          <p>{failure.message}</p>
          <p className="mt-2 text-(--color-ink-3)">
            {failure.scope === 'platform' ? 'The whole platform' : `The report “${failure.scope.replace('report:', '')}”`}{' '}
            is unavailable
            {failure.expectedEndUtc && <> for about {relativeTime(failure.expectedEndUtc)} more</>}.
          </p>
          {onRetry && (
            <div className="mt-4">
              <Button onClick={onRetry}>Check again</Button>
            </div>
          )}
        </Panel>
      )

    case 'unauthenticated':
      return (
        <Panel tone="neutral" title="Session expired">
          <p>Sign in again to continue.</p>
        </Panel>
      )

    case 'config': {
      // The guidance and Microsoft's own text arrive separated by a blank line.
      // Split so the ACTION is prose and the provider's message is quoted below
      // it — the trace and correlation ids in there are what Microsoft support
      // asks for, so they must survive, but they must not be the headline.
      const [action, ...rest] = failure.message.split('\n\n')
      const provider = rest.join('\n\n')
      return (
        <Panel tone="warn" title="Sign-in is not configured yet">
          <p>{action}</p>
          {provider && (
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded border border-(--color-line) bg-(--color-surface-3) px-3 py-2 text-(length:--text-small) text-(--color-ink-3)">
              {provider}
            </pre>
          )}
          <p className="mt-3 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
            Nothing is wrong with your account. This is a setting on the Entra app registration,
            and until it is changed nobody can sign in.
          </p>
        </Panel>
      )
    }

    // A 404 on a report call means the running API does not map the route —
    // in practice a tab or a frontend a deploy ago talking to the other build.
    // It had no case and fell off the switch, so React drew nothing: an empty
    // area and the correlation id thrown away. Reloading picks up the matching
    // build, which is why that is the first advice.
    case 'not-found':
      return (
        <Panel tone="error" title="This view is not available">
          <p>
            The server did not recognise this request. Reload the page; if it persists, send the
            reference below to whoever administers MBI.
          </p>
          {failure.correlationId && <CorrelationId value={failure.correlationId} />}
          {onRetry && (
            <div className="mt-4">
              <Button onClick={onRetry}>Try again</Button>
            </div>
          )}
        </Panel>
      )

    case 'error':
      return (
        <Panel tone="error" title="Something went wrong">
          <p>{failure.title}</p>
          {failure.correlationId && <CorrelationId value={failure.correlationId} />}
          {onRetry && (
            <div className="mt-4">
              <Button onClick={onRetry}>Try again</Button>
            </div>
          )}
        </Panel>
      )

    default: {
      // Exhaustiveness. A new ApiFailure kind without a case above now fails
      // tsc HERE instead of reaching a reader as a blank screen, which is how
      // 'not-found' went unnoticed (tsconfig has no noImplicitReturns).
      const unhandled: never = failure
      void unhandled
      return null
    }
  }
}

function Panel({
  tone,
  title,
  children,
}: {
  tone: 'warn' | 'error' | 'neutral'
  title: string
  children: React.ReactNode
}) {
  // A rule down the LEFT EDGE rather than a bar across the top. It reads as a
  // margin mark on a document instead of a browser notification, which is the
  // right register for a screen explaining an entitlement decision — this is not
  // a transient toast, it is the answer to "why can I not see this".
  const edge = {
    warn: 'border-l-(--color-warn)',
    error: 'border-l-(--color-overdue)',
    neutral: 'border-l-(--color-navy-700)',
  }[tone]

  return (
    <div
      className={`mx-auto mt-6 max-w-2xl overflow-hidden rounded-md border border-l-[3px] border-(--color-line) bg-(--color-surface) shadow-(--shadow-card) ${edge}`}
    >
      <div className="px-6 py-5">
        <h3 className="mb-2 text-(length:--text-lg) font-semibold text-(--color-ink)">{title}</h3>
        <div className="text-(length:--text-base) leading-relaxed text-(--color-ink-2)">{children}</div>
      </div>
    </div>
  )
}
